import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import type { Nullable, Tournament, UserWithToken, WithData } from './api.types';
import type { TournamentPlayerStatus } from './api.types';
import type { HallOfFameEntry } from './ranking';
import { USER_BASIC_FIELDS } from './fragments';

export type SuperYacsLeagueSource = {
  id: number;
  name: string;
  dateTo: string | null;
  winner: {
    name: string | null;
    lastName: string | null;
    cossyId: string | null;
    userId: number | null;
  } | null;
};

// Clasificatorio todavía no disputado: en la landing va como una caja con los
// cupos vacíos ("A definir").
export type SuperYacsPendingTournament = {
  id: number;
  title: string;
  dateFrom: string | null;
};

export type SuperYacsTeamPlayer = {
  name: string;
  secondName: string | null;
  lastName: string;
  secondLastName: string | null;
  cossyId: string | null;
  userId: number;
};

export type SuperYacsTeam = {
  id: number;
  name: string;
  position: number;
  players: SuperYacsTeamPlayer[];
};

// YACS 3 vs 3: clasifican los integrantes del Top 4 de equipos. `pending` = sin
// posiciones cargadas todavía, y `teams` viene vacío.
export type SuperYacsTeamQualifier = {
  id: number;
  title: string;
  dateFrom: string | null;
  pending: boolean;
  teams: SuperYacsTeam[];
};

// Invitado cargado a mano desde el folder, sin torneo de origen. Con cuenta
// trae `userId` + `cossyId`: está pre-inscripto y puede aceptar la invitación.
export type SuperYacsSpecialGuest = {
  name: string;
  /** Invitados especiales u organizadores; cada uno va en su sección. */
  section?: `${SuperYacsQualifierSection}`;
  bio: string;
  /** Mazo con el que ganó el Nacional; null para organizadores. */
  deck?: string | null;
  photo: string | null;
  userId?: number | null;
  cossyId?: string | null;
};

// Estado de la invitación de una identidad al torneo. Vive fuera del payload
// cacheado a propósito: ver el comentario de `getSuperYacsInvitations`.
export type SuperYacsInvitation = {
  userId: number | null;
  cossyId: string | null;
  status: TournamentPlayerStatus;
  invitedByOrganizer: boolean;
};

export type SuperYacsLanding = {
  tournament: Tournament | null;
  hallOfFame: HallOfFameEntry[];
  leagues: SuperYacsLeagueSource[];
  pendingTournaments: SuperYacsPendingTournament[];
  teamQualifiers: SuperYacsTeamQualifier[];
  guestHallOfFame: HallOfFameEntry[];
  specialGuests: SuperYacsSpecialGuest[];
};

// Los clasificados de YACS y los invitados especiales (YCS / Nacional /
// Regional) salen del mismo shape del Salón de la Fama y la landing los arma
// con la misma caja, así que comparten fragment.
const SUPER_YACS_HALL_ENTRY = gql`
  fragment SuperYacsHallEntry on HallOfFameEntry {
    id
    name
    disabled
    tournamentId
    tournament {
      id
      swissId
    }
    winner {
      name
      secondName
      lastName
      secondLastName
      cossyId
      deck
      decklist
      userId
      position
      user {
        id
        picture
      }
    }
    topPlayers {
      name
      secondName
      lastName
      secondLastName
      cossyId
      deck
      decklist
      userId
      position
    }
    date
  }
`;

// Payload agregado y cacheado de la landing /super-yacs. El torneo pide solo
// los campos que consume la landing (no todo el shape pesado de
// publicTournament).
//
// Todo lo que entra acá es tolerante a estar unos minutos viejo. Lo que no lo
// es —el estado de la invitación de cada clasificado— vive en
// `getSuperYacsInvitations`.
export const getSuperYacsLanding = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<SuperYacsLanding>>(
    gql`
      ${USER_BASIC_FIELDS}
      ${SUPER_YACS_HALL_ENTRY}
      query superYacsLanding($tournamentId: Int!) {
        data: superYacsLanding(tournamentId: $tournamentId) {
          tournament {
            id
            title
            status
            dateFrom
            # Con el cierre de la inscripción la landing deja de ofrecer
            # aceptar la invitación (el API la rechaza pasado el cierre).
            preInscriptionDateFrom
            preInscriptionDateTo
            inscriptionDateTo
            nicknamesAllowed
            # Ronda en curso y ventanas cargadas en el torneo: la landing las
            # muestra arriba y arma el cronograma con ellas.
            currentRound
            roundPlannings {
              round
              startDate
              endDate
            }
            # Las del top (cuartos, semifinal, final), planificadas desde el
            # suizo aunque el torneo del top todavía no exista.
            topRoundPlannings {
              round
              startDate
              endDate
            }
            # Los jugadores están acá sólo para la tabla de posiciones, así que
            # el usuario trae lo que necesita getDisplayName y nada más: sin
            # status ni invitedByOrganizer (eso es superYacsInvitations, que no
            # se cachea) y sin cossyId ni IDs de juego, que son identidad y no
            # tienen por qué quedar congelados en tres cachés públicos.
            players {
              id
              user {
                ...UserBasicFields
              }
              results {
                id
                position
                points
                wins
                losses
                draws
                tiebreaker
              }
            }
          }
          hallOfFame {
            ...SuperYacsHallEntry
          }
          guestHallOfFame {
            ...SuperYacsHallEntry
          }
          leagues {
            id
            name
            dateTo
            winner {
              name
              lastName
              cossyId
              userId
            }
          }
          pendingTournaments {
            id
            title
            dateFrom
          }
          teamQualifiers {
            id
            title
            dateFrom
            pending
            teams {
              id
              name
              position
              players {
                name
                secondName
                lastName
                secondLastName
                cossyId
                userId
              }
            }
          }
          specialGuests {
            name
            section
            bio
            deck
            photo
            userId
            cossyId
          }
        }
      }
    `,
    { tournamentId }
  );

/**
 * Estado de las invitaciones del torneo, en vivo.
 *
 * Va aparte de `getSuperYacsLanding` porque el payload de la landing pasa por
 * tres cachés encadenados (Redis, el edge de Vercel y el caché del router), y
 * esto es lo único que no puede estar viejo: si un merge de cuentas le cambia
 * el userId o el cossyId al jugador, la lista vieja deja de matchearlo y el
 * botón de aceptar la invitación no le aparece nunca.
 *
 * Se llama desde el cliente, después de hidratar — desde el loader quedaría
 * congelada en el HTML que cachea el edge, que es justo lo que se quiere
 * evitar.
 */
export const getSuperYacsInvitations = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<SuperYacsInvitation[]>>(
    gql`
      query superYacsInvitations($tournamentId: Int!) {
        data: superYacsInvitations(tournamentId: $tournamentId) {
          userId
          cossyId
          status
          invitedByOrganizer
        }
      }
    `,
    { tournamentId }
  );

// Reset manual del caché de la landing (botón admin en el folder). El ABM ya
// invalida solo, esto queda para cuando cambia algo de afuera (una entrada del
// Salón de la Fama, el resultado de una liga).
export const invalidateSuperYacsLanding = async (
  user: Nullable<UserWithToken>
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation invalidateSuperYacsLanding {
        data: invalidateSuperYacsLanding
      }
    `
  );

// ---------------------------------------------------------------------------
// ABM de clasificatorios (admin)
// ---------------------------------------------------------------------------

export enum SuperYacsQualifierKind {
  TeamTournament = 'TEAM_TOURNAMENT',
  PendingTournament = 'PENDING_TOURNAMENT',
  League = 'LEAGUE',
  HallType = 'HALL_TYPE',
  Guest = 'GUEST',
}

export enum SuperYacsQualifierSection {
  Qualified = 'QUALIFIED',
  Guest = 'GUEST',
  Organizer = 'ORGANIZER',
}

export type SuperYacsQualifier = {
  id: number;
  tournamentId: number;
  kind: SuperYacsQualifierKind;
  section: SuperYacsQualifierSection;
  sourceTournamentId: number | null;
  sourceTournament: {
    id: number;
    title: string | null;
    dateFrom: string | null;
  } | null;
  leagueId: number | null;
  league: { id: number; name: string; dateTo: string | null } | null;
  hallType: string | null;
  guestName: string | null;
  guestBio: string | null;
  guestPhoto: string | null;
  userId: number | null;
  user: {
    id: number;
    name: string;
    lastName: string;
    cossyId: string | null;
  } | null;
  topCount: number | null;
  sortOrder: number;
  isActive: boolean;
};

export type SuperYacsQualifierInput = {
  kind?: SuperYacsQualifierKind;
  section?: SuperYacsQualifierSection;
  sourceTournamentId?: number | null;
  leagueId?: number | null;
  hallType?: string | null;
  guestName?: string | null;
  guestBio?: string | null;
  guestPhoto?: string | null;
  userId?: number | null;
  topCount?: number | null;
  sortOrder?: number;
  isActive?: boolean;
};

const SUPER_YACS_QUALIFIER = gql`
  fragment SuperYacsQualifierFields on SuperYacsQualifier {
    id
    tournamentId
    kind
    section
    sourceTournamentId
    sourceTournament {
      id
      title
      dateFrom
    }
    leagueId
    league {
      id
      name
      dateTo
    }
    hallType
    guestName
    guestBio
    guestPhoto
    userId
    user {
      id
      name
      lastName
      cossyId
    }
    topCount
    sortOrder
    isActive
  }
`;

export const getSuperYacsQualifiers = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<SuperYacsQualifier[]>>(
    gql`
      ${SUPER_YACS_QUALIFIER}
      query superYacsQualifiers($tournamentId: Int!) {
        data: superYacsQualifiers(tournamentId: $tournamentId) {
          ...SuperYacsQualifierFields
        }
      }
    `,
    { tournamentId }
  );

export const createSuperYacsQualifier = async (
  user: Nullable<UserWithToken>,
  input: SuperYacsQualifierInput & {
    tournamentId: number;
    kind: SuperYacsQualifierKind;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<SuperYacsQualifier>>(
    gql`
      ${SUPER_YACS_QUALIFIER}
      mutation createSuperYacsQualifier(
        $input: CreateSuperYacsQualifierInput!
      ) {
        data: createSuperYacsQualifier(input: $input) {
          ...SuperYacsQualifierFields
        }
      }
    `,
    { input }
  );

export const updateSuperYacsQualifier = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: SuperYacsQualifierInput
) =>
  getGraphQLQueryClient(user).request<WithData<SuperYacsQualifier>>(
    gql`
      ${SUPER_YACS_QUALIFIER}
      mutation updateSuperYacsQualifier(
        $id: Int!
        $input: UpdateSuperYacsQualifierInput!
      ) {
        data: updateSuperYacsQualifier(id: $id, input: $input) {
          ...SuperYacsQualifierFields
        }
      }
    `,
    { id, input }
  );

export const deleteSuperYacsQualifier = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteSuperYacsQualifier($id: Int!) {
        data: deleteSuperYacsQualifier(id: $id)
      }
    `,
    { id }
  );
